import { Inject, Injectable } from "@angular/core";
import { AbstractHttpService } from "../http/abstract-http.service";
import { Http } from "@angular/http";
import { Observable, Subject } from "rxjs";
import { ConfigurationServiceConstants } from "./configuration-service-constants";
const stripJsonComments = require('strip-json-comments');
/**
 * This is a cut down version of ConfigurationService, meant to fetch bundled 'static' and local-storage configurations only.
 * It is safe to use without a backend connection to obtain configurations.
 * NOTE: values saved using LocalConfigurationService will not update the cache of ConfigurationService (and vice-versa).
 * For this reason, use of the service should be restricted to special cases where a login has not yet occurred and ConfigurationService has not yet been used.
 */
export class LocalConfigurationService extends AbstractHttpService {
    constructor(localHttp, _application, localConfigFile, _contextNames) {
        super();
        this.localHttp = localHttp;
        this._application = _application;
        this.localConfigFile = localConfigFile;
        this._contextNames = _contextNames;
        this._configurationChanges = new Subject();
        this.pathName = window.location.pathname;
    }
    /**
     * Returns an observable sequence of changed config specs.
     * @returns {Observable}
     */
    get configurationChanges() {
        return this._configurationChanges;
    }
    getString(configSpec, dflt) {
        let match = ConfigurationServiceConstants.configSpecPattern.exec(configSpec);
        if (!match || match[1] !== 'local') {
            return Promise.resolve(null);
        }
        let configName = match[2];
        return new Promise((resolve, reject) => {
            this.loadLocalConfigurations().subscribe((configurations) => {
                if (!configurations.hasOwnProperty(configName)) {
                    resolve(dflt);
                }
                resolve(configurations[configName]);
            });
        });
    }
    getBoolean(configSpec, dflt = false) {
        return this.getString(configSpec).then((value) => value === undefined ? dflt : value.toLowerCase() === 'true');
    }
    getNumber(configSpec, dflt) {
        return this.getString(configSpec).then((value) => {
            if (value === undefined) {
                return dflt;
            }
            let i = parseInt(value);
            return isNaN(i) ? undefined : i;
        });
    }
    /**
     * Saves a local configuration.
     * The configuration is persisted in local browser-based storage against the default context.
     * @param configSpec
     * @param value
     * @param storeInSession Indicates that the value is stored only for the session
     * @returns A boolean (true on success) indicating that the configuration has been saved
     */
    saveString(configSpec, value, storeInSession) {
        let match = ConfigurationServiceConstants.configSpecPattern.exec(configSpec);
        if (!match) {
            return false;
        }
        let category = match[1];
        let configName = match[2];
        if (category !== 'local') {
            return false;
        }
        const storage = storeInSession ? sessionStorage : localStorage;
        let localConfigurations = JSON.parse(storage.getItem(this.localStorageKey)) || {};
        ConfigurationServiceConstants.setConfiguration(localConfigurations, category, configName, "default", {}, value);
        storage.setItem(this.localStorageKey, JSON.stringify(localConfigurations));
        // Invalidate cache - reload is inexpensive and saves should be rare
        this.localConfigurationsObservable = null;
        this._configurationChanges.next(configSpec);
        return true;
    }
    saveBoolean(configSpec, value, storeInSession) {
        return this.saveString(configSpec, value.toString(), storeInSession);
    }
    saveNumber(configSpec, value, storeInSession) {
        return this.saveString(configSpec, value.toString(), storeInSession);
    }
    get localStorageKey() {
        return this.pathName + "/" + ConfigurationServiceConstants.LOCAL_STORAGE_KEY;
    }
    /**
     * Returns an observable for the configuration data from the local configuration file.
     * Multiple concurrent calls to this method will return the same observable, so that the data will not be fetched multiple times.
     * @returns {any}
     */
    loadLocalConfigurations() {
        // Only allow a single concurrent request
        if (this.localConfigurationsObservable) {
            return this.localConfigurationsObservable;
        }
        if (!this.localConfigFile) {
            return Observable.of({});
        }
        return this.localConfigurationsObservable = this.localHttp.get(this.localConfigFile)
            .catch((response) => this.handleServiceError(response))
            .map((response) => {
            let configurations = JSON.parse(stripJsonComments(response.text()));
            // local storage (saved) configurations
            this.loadFromStorage(configurations, localStorage);
            // session storage (saved) configurations
            this.loadFromStorage(configurations, sessionStorage);
            return configurations;
        })
            .publishReplay(1)
            .refCount();
    }
    loadFromStorage(configurations, storage) {
        let localConfigurations = JSON.parse(storage.getItem(this.localStorageKey)) || {};
        if (localConfigurations.hasOwnProperty("local")) {
            let configurationMap = localConfigurations["local"];
            for (let configName in configurationMap) {
                if (configurationMap.hasOwnProperty(configName)) {
                    let contextSpecMap = configurationMap[configName];
                    if (contextSpecMap.hasOwnProperty("default")) {
                        configurations[configName] = contextSpecMap["default"];
                    }
                }
            }
        }
    }
    get application() {
        return this._application;
    }
    get contextNames() {
        return this._contextNames;
    }
}
LocalConfigurationService.decorators = [
    { type: Injectable },
];
/** @nocollapse */
LocalConfigurationService.ctorParameters = () => [
    { type: Http, },
    { type: undefined, decorators: [{ type: Inject, args: [ConfigurationServiceConstants.APPLICATION_KEY,] },] },
    { type: undefined, decorators: [{ type: Inject, args: [ConfigurationServiceConstants.LOCAL_CONFIG_FILE_KEY,] },] },
    { type: Array, decorators: [{ type: Inject, args: [ConfigurationServiceConstants.CONTEXT_NAMES_KEY,] },] },
];
//# sourceMappingURL=local-configuration.service.js.map


//////////////////
// WEBPACK FOOTER
// ./node_modules/@fedelta-pos/data-client/configuration/local-configuration.service.js
// module id = 50
// module chunks = 1